// imports
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Divider, styled } from '@mui/material';

// local imports
import ReusableAccordion from '../ReusableAccordion/ReusableAccordion';
import ReusableMenuTypography from './ReusableMenuTypography/ReusableMenuTypography';
import SectorMenu from './SectorMenu/SectorMenu';
import ModelMenu from './ModelMenu/ModelMenu';
import VariableMenu from './VariableMenu/VariableMenu';
import TemporalResolutionMenu from './TemporalResolutionMenu/TemporalResolutionMenu';
import DateMenu from './DateMenu/DateMenu';
import UpdateMapButton from './UpdateMapButton/UpdateMapButton';
import { initialWaterDataState } from '../../../store/waterDataSlice';
import OpacityControl from '../OpacityControl/OpacityControl';
import { setOpacity } from '../../../store/waterDataSlice';
import { RootState } from '../../../store';
import { getAvailableTemporalResolutions } from '../../../constants/TemporalResolution';
import { getUrlParam } from '../../../utils/getUrlParam';
import { useUrlParameters } from '../../../hooks/useUrlParameters';

const StyledDivider = styled(Divider)(() => ({
  marginTop: '15px',
  marginBottom: '10px',
}));

interface WaterDataAccordionProps {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, newExpanded: boolean) => void;
}

export default function WaterDataAccordion({
  expanded,
  onChange,
}: WaterDataAccordionProps) {
  // Initialize local state from URL params first, falling back to Redux initial state
  // This ensures URL parameters take precedence over Redux defaults
  const [selectedSectorLocal, setSelectedSectorLocal] = React.useState(
    () => getUrlParam('sector') ?? initialWaterDataState.selectedSector
  );
  const [selectedModelLocal, setSelectedModelLocal] = React.useState<
    string | null
  >(() => getUrlParam('model') ?? initialWaterDataState.selectedModel);
  const [selectedVariableLocal, setSelectedVariableLocal] = React.useState<
    string | null
  >(() => getUrlParam('variable') ?? initialWaterDataState.selectedVariable);
  const [selectedStartMonthLocal, setSelectedStartMonthLocal] = React.useState<
    string | null
  >(
    () => getUrlParam('startMonth') ?? initialWaterDataState.selectedStartMonth
  );
  const [selectedStartYearLocal, setSelectedStartYearLocal] = React.useState<
    string | null
  >(() => getUrlParam('startYear') ?? initialWaterDataState.selectedStartYear);
  const [selectedEndMonthLocal, setSelectedEndMonthLocal] = React.useState<
    string | null
  >(() => getUrlParam('endMonth') ?? initialWaterDataState.selectedEndMonth);
  const [selectedEndYearLocal, setSelectedEndYearLocal] = React.useState<
    string | null
  >(() => getUrlParam('endYear') ?? initialWaterDataState.selectedEndYear);

  // Initialize isMonthly from URL param first, falling back to Redux initial state
  const [isMonthly, setIsMonthly] = React.useState(() => {
    const availableResolutions = getAvailableTemporalResolutions();
    const temporalResolution =
      getUrlParam('temporalResolution') ??
      initialWaterDataState.temporalResolution;
    const validResolution =
      temporalResolution in availableResolutions
        ? temporalResolution
        : initialWaterDataState.temporalResolution;
    return validResolution === 'monthly';
  });
  // Track temporal resolution for passing to UpdateMapButton
  const [selectedTemporalResolutionLocal, setSelectedTemporalResolutionLocal] =
    React.useState(() => {
      // Get the valid temporal resoutions for the current build
      const availableResolutions = getAvailableTemporalResolutions();
      // Get parameter from the url, default to 'monthly' if it doesn't exist
      const temporalResolution =
        getUrlParam('temporalResolution') ??
        initialWaterDataState.temporalResolution;
      // Check if the url parameter is one of the valid temporal resolutions
      // If not, default to 'monthly'
      return temporalResolution in availableResolutions
        ? temporalResolution
        : initialWaterDataState.temporalResolution;
    });
  // Track useDateRange for passing to UpdateMapButton
  const [useDateRangeLocal, setUseDateRangeLocal] = React.useState(() => {
    const urlUseDateRange = getUrlParam('useDateRange');
    return urlUseDateRange === 'true';
  });
  const [isDateAlertVisible, setIsDateAlertVisible] = React.useState(false);
  const dispatch = useDispatch();
  const { setParameter } = useUrlParameters();
  const {
    opacity,
    appliedSector,
    appliedModel,
    appliedVariable,
    appliedTemporalResolution,
    appliedStartMonth,
    appliedStartYear,
  } = useSelector((state: RootState) => state.waterData);

  // Check if form state matches applied state (what's currently on the map)
  const isFormStateChanged = React.useMemo(() => {
    return (
      selectedSectorLocal !== appliedSector ||
      selectedModelLocal !== appliedModel ||
      selectedVariableLocal !== appliedVariable ||
      (isMonthly ? 'monthly' : 'annual') !== appliedTemporalResolution ||
      (isMonthly ? selectedStartMonthLocal : null) !== appliedStartMonth ||
      selectedStartYearLocal !== appliedStartYear
    );
  }, [
    selectedSectorLocal,
    selectedModelLocal,
    selectedVariableLocal,
    isMonthly,
    selectedStartMonthLocal,
    selectedStartYearLocal,
    appliedSector,
    appliedModel,
    appliedVariable,
    appliedTemporalResolution,
    appliedStartMonth,
    appliedStartYear,
  ]);

  const handleAlertVisibilityChange = (isVisible: boolean) => {
    setIsDateAlertVisible(isVisible);
  };

  const handleOpacityChange = (newOpacity: number) => {
    dispatch(setOpacity(newOpacity));
    setParameter('opacity', String(newOpacity));
  };

  return (
    <ReusableAccordion
      title="NWDC Datasets"
      expanded={expanded}
      onChange={onChange}
      // minHeight prevents layout shift when dropdown menus within the accordion open and close
      accordionStyles={{ '& .MuiAccordionDetails-root': { minHeight: 280 } }}
    >
      <SectorMenu setSelectedSectorLocal={setSelectedSectorLocal} />
      <ModelMenu
        selectedSector={selectedSectorLocal}
        setSelectedModel={setSelectedModelLocal}
      />
      <VariableMenu
        selectedSector={selectedSectorLocal}
        selectedModel={selectedModelLocal}
        setSelectedVariable={setSelectedVariableLocal}
      />
      {/* TODO: Delete the import.meta.env.DEV condition once time series is functional. */}
      {/* For now, only show the temporal resolution component in Dev. */}
      {import.meta.env.DEV && (
        <TemporalResolutionMenu
          setIsMonthly={setIsMonthly}
          setSelectedTemporalResolution={setSelectedTemporalResolutionLocal}
        />
      )}
      <DateMenu
        selectedModel={selectedModelLocal!}
        setStartMonth={setSelectedStartMonthLocal}
        setStartYear={setSelectedStartYearLocal}
        setEndMonth={setSelectedEndMonthLocal}
        setEndYear={setSelectedEndYearLocal}
        isMonthly={isMonthly}
        onAlertVisibilityChange={handleAlertVisibilityChange}
        onUseDateRangeChange={setUseDateRangeLocal}
      />
      <UpdateMapButton
        selectedSector={selectedSectorLocal}
        selectedModel={selectedModelLocal}
        selectedVariable={selectedVariableLocal}
        selectedTemporalResolution={selectedTemporalResolutionLocal}
        selectedStartMonth={selectedStartMonthLocal}
        selectedStartYear={selectedStartYearLocal}
        selectedEndMonth={selectedEndMonthLocal}
        selectedEndYear={selectedEndYearLocal}
        useDateRange={useDateRangeLocal}
        isDateAlertVisible={isDateAlertVisible}
        isDisabled={!isFormStateChanged}
        isMonthly={isMonthly}
      />
      <StyledDivider />
      <ReusableMenuTypography>Data Layer Opacity</ReusableMenuTypography>
      <OpacityControl
        value={opacity}
        onChange={handleOpacityChange}
        min={0}
        data-testid="awd-opacity-control"
      />
    </ReusableAccordion>
  );
}
